import type { Survey, SurveyAttribute, SurveyViewingPermission } from '@engagement-web-app/common';
import type { UseQueryResult } from '@tanstack/react-query';
import classNames from 'classnames';
import { useState } from 'react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { TextButton } from '@/components/TextButton';
import { combineQueryErrors } from '@/utils';
import * as styles from './index.css';

/** What a save writes for one survey. */
export type SurveyPermissionInput = {
    readonly canViewComments: boolean;
    readonly attributes: { attributeId: string; optionIds: string[] }[];
};

type SurveyPermissionEditorProps = {
    /** The survey being edited. */
    survey: Survey;
    attributes: UseQueryResult<SurveyAttribute[]>;
    permissions: UseQueryResult<{ unrestricted: boolean; permissions: SurveyViewingPermission[] }>;
    isSaving?: boolean;
    onSave: (surveyId: string, input: SurveyPermissionInput) => void;
    onRevoke: (surveyId: string) => void;
};

/**
 * Edits one user's per-survey viewing permissions. Each survey is saved on its
 * own, matching the API — granting is a save, taking access away is a revoke.
 */
export function SurveyPermissionEditor({
    survey,
    attributes,
    permissions,
    isSaving = false,
    onSave,
    onRevoke,
}: SurveyPermissionEditorProps) {
    const errors = combineQueryErrors(permissions, attributes);
    if (errors.isError) return <ErrorOverlay active onRetry={errors.retry} message="閲覧権限の取得に失敗しました。" />;
    if (permissions.status !== 'success' || attributes.status !== 'success') return <SurveyPermissionEditorSkeleton />;

    return (
        <SurveyPermissionEditorContent
            survey={survey}
            attributes={attributes.data}
            unrestricted={permissions.data.unrestricted}
            permissions={permissions.data.permissions}
            isSaving={isSaving}
            onSave={onSave}
            onRevoke={onRevoke}
        />
    );
}

type SurveyPermissionEditorContentProps = Omit<SurveyPermissionEditorProps, 'permissions' | 'attributes'> & {
    unrestricted: boolean;
    permissions: SurveyViewingPermission[];
    attributes: SurveyAttribute[];
};

// Split from the buffer above so its rendering stays free of loading concerns.
function SurveyPermissionEditorContent({
    survey,
    attributes,
    unrestricted,
    permissions,
    isSaving = false,
    onSave,
    onRevoke,
}: SurveyPermissionEditorContentProps) {
    if (unrestricted) {
        return (
            <Card withBorder>
                <Flex direction="column" gap="2">
                    <Pill variant="secondary">全サーベイ閲覧可</Pill>
                    <p className={styles.note}>
                        管理者はサーベイ単位の閲覧権限による制限を受けません。制限をかけるにはロールをメンバーに変更してください。
                    </p>
                </Flex>
            </Card>
        );
    }

    const permission = permissions.find((p) => p.surveyId === survey.surveyId) ?? null;

    return (
        <SurveyPermissionCard
            // Re-mounts when the selected survey or its stored permission
            // changes, so the draft below always starts from what the server
            // holds.
            key={`${survey.surveyId}:${JSON.stringify(permission)}`}
            survey={survey}
            attributes={attributes}
            permission={permission}
            isSaving={isSaving}
            onSave={onSave}
            onRevoke={onRevoke}
        />
    );
}

export function SurveyPermissionEditorSkeleton() {
    return <SurveyPermissionCardSkeleton />;
}

type SurveyPermissionCardProps = {
    survey: Survey;
    attributes: SurveyAttribute[];
    permission: SurveyViewingPermission | null;
    isSaving: boolean;
    onSave: (surveyId: string, input: SurveyPermissionInput) => void;
    onRevoke: (surveyId: string) => void;
};

const optionKey = (attributeId: string, optionId: string) => `${attributeId}:${optionId}`;

function SurveyPermissionCard({
    survey,
    attributes,
    permission,
    isSaving,
    onSave,
    onRevoke,
}: SurveyPermissionCardProps) {
    const [granted, setGranted] = useState(permission !== null);
    const [canViewComments, setCanViewComments] = useState(permission?.canViewComments ?? false);
    const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
        new Set(
            permission?.attributes.flatMap((attribute) =>
                attribute.optionIds.map((optionId) => optionKey(attribute.attributeId, optionId))
            ) ?? []
        )
    );

    const toggleOption = (attributeId: string, optionId: string) => {
        setSelectedOptions((current) => {
            const next = new Set(current);
            const key = optionKey(attributeId, optionId);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleAttribute = (attribute: SurveyAttribute, checked: boolean) => {
        setSelectedOptions((current) => {
            const next = new Set(current);
            for (const option of attribute.options) {
                const key = optionKey(attribute.id, option.id);
                if (checked) {
                    next.add(key);
                } else {
                    next.delete(key);
                }
            }
            return next;
        });
    };

    /*
     * Holding a permission is what grants the survey, so unchecking "閲覧できる"
     * and saving is the revoke — there is no separate button for it.
     */
    const handleSave = () => {
        if (!granted) {
            if (permission) onRevoke(survey.surveyId);
            return;
        }

        onSave(survey.surveyId, {
            canViewComments,
            attributes: attributes
                .map((attribute) => ({
                    attributeId: attribute.id,
                    optionIds: attribute.options
                        .filter((option) => selectedOptions.has(optionKey(attribute.id, option.id)))
                        .map((option) => option.id),
                }))
                .filter((attribute) => attribute.optionIds.length > 0),
        });
    };

    const checkboxRowClass = (disabled: boolean) =>
        classNames(styles.checkboxRow, { [styles.checkboxRowDisabled]: disabled });

    return (
        <>
            <Section>
                <SectionHeader
                    title={`${survey.surveyName}の閲覧設定`}
                    extras={
                        <Pill variant={permission ? 'secondary' : 'tertiary'}>
                            {permission ? '閲覧可' : '閲覧不可'}
                        </Pill>
                    }
                />

                <Flex direction="column" gap="4">
                    <Flex direction="column" gap="2">
                        <label className={styles.checkboxRow} htmlFor={`granted-${survey.surveyId}`}>
                            <Checkbox
                                id={`granted-${survey.surveyId}`}
                                checked={granted}
                                onCheckedChange={(checked) => setGranted(checked === true)}
                            />
                            <span>このサーベイを閲覧できる</span>
                        </label>

                        <label className={checkboxRowClass(!granted)} htmlFor={`comments-${survey.surveyId}`}>
                            <Checkbox
                                id={`comments-${survey.surveyId}`}
                                checked={canViewComments}
                                disabled={!granted}
                                onCheckedChange={(checked) => setCanViewComments(checked === true)}
                            />
                            <span>コメントを閲覧できる</span>
                        </label>
                    </Flex>

                    <div>
                        <Button variant="primary" onClick={handleSave} disabled={isSaving || (!granted && !permission)}>
                            保存する
                        </Button>
                    </div>
                </Flex>
            </Section>

            <Section>
                <SectionHeader title="選択できる属性" />

                <Flex direction="column" gap="6">
                    {attributes.length === 0 ? (
                        <p className={styles.note}>このサーベイには属性が設定されていません。</p>
                    ) : (
                        <Flex direction="column" gap="3">
                            {attributes.map((attribute) => {
                                const checkedCount = attribute.options.filter((option) =>
                                    selectedOptions.has(optionKey(attribute.id, option.id))
                                ).length;

                                const allChecked = checkedCount === attribute.options.length;

                                return (
                                    <div key={attribute.id} className={styles.attributeGroup}>
                                        <Flex align="center" justify="space-between" gap="3">
                                            <Flex align="center" gap="2">
                                                <span className={styles.attributeName}>{attribute.name}</span>
                                                <span className={styles.selectedCount}>
                                                    {checkedCount}/{attribute.options.length} 選択中
                                                </span>
                                            </Flex>

                                            <TextButton
                                                variant={allChecked ? 'danger' : 'primary'}
                                                onClick={() => toggleAttribute(attribute, !allChecked)}
                                                disabled={!granted}
                                            >
                                                {allChecked ? '全て解除' : '全て選択'}
                                            </TextButton>
                                        </Flex>

                                        <Flex gap="4" wrap="wrap" className={styles.optionList}>
                                            {attribute.options.map((option) => (
                                                <label
                                                    key={option.id}
                                                    className={checkboxRowClass(!granted)}
                                                    htmlFor={`opt-${survey.surveyId}-${option.id}`}
                                                >
                                                    <Checkbox
                                                        id={`opt-${survey.surveyId}-${option.id}`}
                                                        checked={selectedOptions.has(
                                                            optionKey(attribute.id, option.id)
                                                        )}
                                                        disabled={!granted}
                                                        onCheckedChange={() => toggleOption(attribute.id, option.id)}
                                                    />
                                                    <span>{option.name}</span>
                                                </label>
                                            ))}
                                        </Flex>
                                    </div>
                                );
                            })}
                        </Flex>
                    )}

                    <div>
                        <Button variant="primary" onClick={handleSave} disabled={isSaving || (!granted && !permission)}>
                            保存する
                        </Button>
                    </div>
                </Flex>
            </Section>
        </>
    );
}

function SurveyPermissionCardSkeleton() {
    return (
        <>
            <Flex as={Section} direction="column" gap="6">
                <Flex align="center" justify="space-between" gap="4">
                    <Flex align="center" gap="3">
                        <Skeleton.Line width={160} height="base" />
                        <Skeleton.Box width={64} height={24} />
                    </Flex>
                </Flex>

                <Flex direction="column" gap="2">
                    <Skeleton.Line width={180} height="sm" />
                    <Skeleton.Line width={160} height="sm" />
                </Flex>

                <Skeleton.Box width={100} height={36} />
            </Flex>

            <Flex as={Section} direction="column" gap="6">
                <Flex direction="column" gap="3">
                    <Skeleton.Line width={120} height="sm" />
                    {Array.from({ length: 4 }).map((_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, rows have no identity
                        <Skeleton.Box key={i} width="100%" height={90} />
                    ))}
                </Flex>
            </Flex>
        </>
    );
}
